/**
 * Feed Composer model — explicit state definition.
 *
 * Elm-ish architecture: state is explicit and typed.
 * No implicit state machines encoded in useState hooks.
 *
 * Security posture:
 * - No account_id, legal name, phone, email, address in this model
 * - Actor identity is server-derived from authenticated session
 * - Post content is never logged or included in error messages
 */
import { MAX_CONTENT_LENGTH } from '@/api/feedPosts';

// =============================================================================
// Constants
// =============================================================================

/**
 * Maximum character count per S010.
 *
 * IMPORTANT: This MUST be verified against the real backend semantics.
 * The backend may count UTF-8 bytes, Unicode code points, or grapheme clusters.
 * JavaScript string.length does NOT always match backend semantics.
 * See: ACT-INDEEP-FRONTEND-FEED-COMPOSER-API-COMPOSITION01
 */
export const POST_MAX_LENGTH = MAX_CONTENT_LENGTH; // 500

// =============================================================================
// Types
// =============================================================================

/**
 * Author summary for display in the composer.
 * Derived from the user's profile (sponsor or friend).
 */
export interface AuthorSummary {
  /** Display name from profile.display_name */
  readonly displayName: string;
  /** Age from profile.age (null when not set) */
  readonly age: number | null;
  /** Location from profile.city_or_region (null when not set) */
  readonly location: string | null;
  /** First letter of displayName, uppercase, for avatar */
  readonly initials: string;
}

/**
 * Validation error types for post content.
 */
export type ComposerValidationError =
  /** Content field is empty */
  | 'content_empty'
  /** Content is only whitespace */
  | 'content_whitespace'
  /** Content exceeds character limit */
  | 'content_too_long';

/**
 * Lifecycle status values for the feed composer program.
 */
export type FeedComposerStatus =
  | 'idle'        // Initial state before loading author data
  | 'loading'     // Loading author profile data
  | 'form'        // Showing the composer form
  | 'submitting'  // Submitting the post to the backend
  | 'success'     // Post created successfully
  | 'error';      // An error occurred

/**
 * Complete state shape for the Feed Composer program.
 *
 * CORRECTION02: nextSubmissionId is a deterministic counter for pure submission
 * identity (no Date.now()). activeSubmissionId tracks the in-flight submission
 * for stale-completion suppression (R5).
 */
export interface FeedComposerModel {
  /** Current lifecycle status */
  readonly status: FeedComposerStatus;
  /** Author profile data (null when loading or no profile) */
  readonly author: AuthorSummary | null;
  /** Post text content */
  readonly content: string;
  /** Whether to post anonymously */
  readonly anonymous: boolean;
  /** Character count (derived from content using countCharacters) */
  readonly charCount: number;
  /** Validation errors for the current content */
  readonly validationErrors: readonly ComposerValidationError[];
  /** Error message when status is 'error' */
  readonly error: string | null;
  /** Post ID on successful creation */
  readonly postId: string | null;
  /**
   * Deterministic submission counter — generates IDs inside the pure reducer.
   * Starts at 1. Each accepted publish consumes the current value and increments.
   * Non-reusable: once assigned to activeSubmissionId it is consumed.
   */
  readonly nextSubmissionId: number;
  /**
   * Active submission identity.
   * Non-null when a submission is in-flight.
   * Used to ignore stale completions (R5).
   */
  readonly activeSubmissionId: number | null;
}

// =============================================================================
// Initial state
// =============================================================================

/**
 * Initial/empty state for the feed composer program.
 */
export const initialModel: FeedComposerModel = {
  status: 'idle',
  author: null,
  content: '',
  anonymous: false,
  charCount: 0,
  validationErrors: [],
  error: null,
  postId: null,
  nextSubmissionId: 1,
  activeSubmissionId: null,
};

// =============================================================================
// Pure helper functions
// =============================================================================

/**
 * Count characters in text using JavaScript string.length semantics.
 *
 * IMPORTANT: Backend semantics for the 500-character limit are unknown.
 * The backend may count UTF-8 bytes, Unicode code points, or grapheme clusters.
 * JavaScript string.length does NOT always match backend semantics.
 *
 * This function MUST be verified against the real backend.
 * See: ACT-INDEEP-FRONTEND-FEED-COMPOSER-API-COMPOSITION01
 */
export function countCharacters(text: string): number {
  return text.length;
}

/**
 * Derive author initials from display name.
 * Takes the first Unicode character that is a letter, uppercase.
 */
export function deriveInitials(displayName: string): string {
  if (!displayName || displayName.trim().length === 0) {
    return '?';
  }
  const first = displayName.trim()[0] ?? '';
  return first.toUpperCase();
}

/**
 * Build AuthorSummary from profile data.
 */
export function buildAuthorSummary(params: {
  displayName: string;
  age: number | null;
  location: string | null;
}): AuthorSummary {
  return {
    displayName: params.displayName,
    age: params.age,
    location: params.location,
    initials: deriveInitials(params.displayName),
  };
}

/**
 * Validate post content.
 * Returns array of validation errors (empty if valid).
 *
 * IMPORTANT: Character counting uses JavaScript string.length.
 * Backend semantics MUST be verified. See countCharacters() docstring.
 */
export function validateContent(
  content: string
): readonly ComposerValidationError[] {
  const errors: ComposerValidationError[] = [];
  const trimmed = content.trim();

  if (content.length === 0) {
    errors.push('content_empty');
  } else if (trimmed.length === 0) {
    errors.push('content_whitespace');
  } else if (content.length > POST_MAX_LENGTH) {
    errors.push('content_too_long');
  }

  return errors;
}

/**
 * Check if the form is valid for submission.
 * Requires: no validation errors AND character count > 0.
 */
export function isPublishable(model: FeedComposerModel): boolean {
  return (
    model.validationErrors.length === 0 &&
    model.content.trim().length > 0 &&
    model.content.length <= POST_MAX_LENGTH
  );
}

/**
 * Check if the program is in a terminal state.
 * Terminal states do not accept further user interaction.
 */
export function isTerminalState(model: FeedComposerModel): boolean {
  return model.status === 'success';
}

/**
 * Check if the program is in a state where the form is shown.
 */
export function isFormState(model: FeedComposerModel): boolean {
  return (
    model.status === 'form' ||
    model.status === 'submitting' ||
    model.status === 'error'
  );
}

/**
 * Check if the submit button should be disabled.
 */
export function isSubmitDisabled(model: FeedComposerModel): boolean {
  if (model.status === 'submitting') return true;
  if (model.status === 'success') return true;
  return !isPublishable(model);
}
